import React from "react";
import cx from "classnames";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { BRANDS, MODES } from "../../configs";
import {
	FLIGHTS_CLASS_ID,
	FLIGHTS_PASSENGERS_ID,
	HOTELS_ROOM_AND_GUEST_FIELD_ID,
	PACKAGES_ROOM_AND_GUESTS_FIELD_ID
} from "../../configs/const";
import {
	isAndroid,
	isBrowser,
	isIOS,
	isIphonePro,
	isIphoneProMax
} from "../../views/Shared/utils/detect-devices";

const shouldRenderCloseIcon = ({
	defaultCloseIcon,
	isStackMobileLayout,
	id
}) => {
	const excludedIds = [
		FLIGHTS_CLASS_ID,
		FLIGHTS_PASSENGERS_ID,
		PACKAGES_ROOM_AND_GUESTS_FIELD_ID,
		HOTELS_ROOM_AND_GUEST_FIELD_ID
	];
	return defaultCloseIcon || (isStackMobileLayout && !excludedIds.includes(id));
};

const getPopoverClassNames = ({ id, brand, styles, isStackMobileLayout }) =>
	cx({
		[styles[brand]]: styles[brand],
		[styles.popoverNoInputField]:
			id === PACKAGES_ROOM_AND_GUESTS_FIELD_ID && !isBrowser(),
		[styles.popoverPackagesTabChrome]:
			id === PACKAGES_ROOM_AND_GUESTS_FIELD_ID && isBrowser() && !isIOS(),
		[styles.popoverPackagesTabAndroid]:
			id === PACKAGES_ROOM_AND_GUESTS_FIELD_ID && isAndroid() && !isIOS(),
		[styles.popoverHotelTab]:
			id === HOTELS_ROOM_AND_GUEST_FIELD_ID && isIOS() && !isBrowser(),
		[styles.popoverHotelTabChrome]:
			id === HOTELS_ROOM_AND_GUEST_FIELD_ID && isBrowser() && !isIOS(),
		[styles.popoverHotelTabAndroid]:
			id === HOTELS_ROOM_AND_GUEST_FIELD_ID &&
			isAndroid() &&
			!isBrowser() &&
			!isIOS(),
		[styles.popoverFlightTabChrome]:
			[FLIGHTS_PASSENGERS_ID, FLIGHTS_CLASS_ID].includes(id) &&
			isBrowser() &&
			!isIOS(),
		[styles.popoverFlightTabChromeAndroid]:
			[FLIGHTS_PASSENGERS_ID, FLIGHTS_CLASS_ID].includes(id) &&
			isAndroid() &&
			!isIOS(),
		[styles.popoverFlightTabIos]:
			[FLIGHTS_PASSENGERS_ID, FLIGHTS_CLASS_ID].includes(id) && isIOS(),
		[styles.popoverFlightTabIosMax]:
			[FLIGHTS_PASSENGERS_ID, FLIGHTS_CLASS_ID].includes(id) &&
			isIphoneProMax(),
		[styles.popoverStackLayout]: isStackMobileLayout,
		[styles.popoverStackLayoutClassList]:
			isStackMobileLayout && id === FLIGHTS_CLASS_ID
	});

const getContainerClassNames = ({
	id,
	brand,
	styles,
	showPopover,
	isSOVMobile,
	mode
}) =>
	cx(styles.container, {
		[styles.open]: showPopover,
		[styles[brand]]: styles[brand],
		[styles.containerOffset]: id === FLIGHTS_PASSENGERS_ID,
		[styles.containerOffsetNotFlightClassList]: ![
			FLIGHTS_CLASS_ID,
			FLIGHTS_PASSENGERS_ID
		].includes(id),
		[styles.containerClassListAndRoomsIos]:
			[FLIGHTS_CLASS_ID, FLIGHTS_PASSENGERS_ID].includes(id) &&
			isSOVMobile &&
			isIOS(),
		[styles.containerClassListAndRoomsIosPro]:
			[FLIGHTS_CLASS_ID, FLIGHTS_PASSENGERS_ID].includes(id) &&
			isSOVMobile &&
			isIphonePro(),
		[styles.containerTabView]: mode === MODES.TABS
	});

const FocusTrapChildren = props => {
	const {
		attrs,
		id,
		mode,
		brand,
		isSOVMobile,
		showPopover,
		isStackMobileLayout,
		defaultCloseIcon,
		popoverClassName,
		styles,
		inputWidth,
		containerWidth,
		handleClosePopover,
		children
	} = props;

	const popoverStyles = {
		minWidth: inputWidth,
		maxWidth: containerWidth
	};

	return (
		<div
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...attrs}
			className={cx(
				popoverClassName,
				styles.popover,
				brand === BRANDS.SOV
					? getPopoverClassNames({ id, brand, styles, isStackMobileLayout })
					: styles[brand] && styles[brand]
			)}
			style={popoverStyles}>
			<div
				// eslint-disable-next-line react/jsx-props-no-spreading
				{...(brand !== BRANDS.SOV ? { "body-scroll-lock-ignore": "" } : {})}
				className={getContainerClassNames({
					id,
					brand,
					styles,
					showPopover,
					isSOVMobile,
					mode
				})}>
				{shouldRenderCloseIcon({
					defaultCloseIcon,
					isStackMobileLayout,
					id
				}) && (
					<button
						type="button"
						className={styles.closeButton}
						onClick={handleClosePopover}>
						<FontAwesomeIcon icon={faTimes} className={styles.closeIcon} />
					</button>
				)}
				{children}
			</div>
		</div>
	);
};

FocusTrapChildren.propTypes = {
	attrs: PropTypes.object,
	id: PropTypes.string.isRequired,
	mode: PropTypes.string,
	brand: PropTypes.string,
	inputWidth: PropTypes.string,
	containerWidth: PropTypes.string,
	defaultCloseIcon: PropTypes.bool,
	isSOVMobile: PropTypes.bool,
	showPopover: PropTypes.bool,
	isStackMobileLayout: PropTypes.bool,
	popoverClassName: PropTypes.string,
	styles: PropTypes.object,
	handleClosePopover: PropTypes.func,
	children: PropTypes.oneOfType([
		PropTypes.func,
		PropTypes.node,
		PropTypes.arrayOf(PropTypes.node)
	]).isRequired
};

FocusTrapChildren.defaultProps = {
	attrs: undefined,
	brand: BRANDS.SWG,
	mode: MODES.SEARCH,
	inputWidth: undefined,
	containerWidth: undefined,
	defaultCloseIcon: true,
	popoverClassName: undefined,
	styles: undefined,
	showPopover: false,
	isSOVMobile: false,
	isStackMobileLayout: false,
	handleClosePopover: () => {}
};

export { FocusTrapChildren };
export default FocusTrapChildren;
